import axios from 'axios';
import { logger } from '@/utils/logger';
import { getApiBaseUrl } from '@/utils/apiBase';

const baseURL = getApiBaseUrl();

// Debug log to verify the URL being used

const api = axios.create({
  baseURL: `${baseURL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token and brand context
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Add brand_id header from selected context
    const selectedContext = localStorage.getItem('selectedContext');
    if (selectedContext) {
      try {
        const context = JSON.parse(selectedContext);
        if (context?.brandId) {
          config.headers['X-Brand-ID'] = context.brandId;
        }
      } catch (e) {
        logger.error('Failed to parse selectedContext', e);
      }
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Format error.response.data.detail if it's an object (Pydantic validation errors)
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      
      // If detail is an array of Pydantic errors, format them
      if (Array.isArray(detail)) {
        const messages = detail.map(err => {
          if (typeof err === 'string') return err;
          // Pydantic error format: { type, loc, msg, input, url }
          const location = err.loc ? err.loc.join(' → ') : 'field';
          const message = err.msg || 'Invalid value';
          return `${location}: ${message}`;
        });
        error.response.data.detail = messages.join('; ');
      }
      // If detail is an object (single error), format it
      else if (typeof detail === 'object' && detail !== null) {
        if (detail.msg) {
          const location = detail.loc ? detail.loc.join(' → ') : '';
          error.response.data.detail = location ? `${location}: ${detail.msg}` : detail.msg;
        } else if (detail.message) {
          error.response.data.detail = detail.message;
        } else {
          // Fallback: stringify the object
          try {
            error.response.data.detail = JSON.stringify(detail);
          } catch {
            error.response.data.detail = 'An error occurred';
          }
        }
      }
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token found');
        }

        // Send refresh_token in body (not the expired access_token in header)
        const response = await axios.post(
          `${baseURL}/api/auth/refresh`,
          { refresh_token: refreshToken },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const newToken = response.data.access_token;
        localStorage.setItem('access_token', newToken);
        
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        
        if (response.data.user) {
          localStorage.setItem('user', JSON.stringify(response.data.user));
        }

        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        localStorage.removeItem('selectedContext');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  getCurrentUser: () => api.get('/auth/me'),
  getUserPermissions: () => api.get('/auth/me/permissions'),
  refreshToken: () => api.post('/auth/refresh'),
  updateProfile: (data) => api.put('/auth/me/profile', data),
};

export const usersApi = {
  create: (data) => api.post('/auth/signup', data),
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
};

export const rolesApi = {
  create: (data) => api.post('/roles', data),
  getAll: () => api.get('/roles'),
  getById: (id) => api.get(`/roles/${id}`),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

export const permissionsApi = {
  create: (data) => api.post('/permissions', data),
  getAll: (params) => api.get('/permissions', { params }),
  delete: (id) => api.delete(`/permissions/${id}`),
};

export const clientsApi = {
  create: (data) => api.post('/clients', data),
  getAll: (params) => api.get('/clients', { params }),
  getById: (id) => api.get(`/clients/${id}`),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`, { params: { confirm: true } }),
  cascadePreview: (id) => api.get(`/clients/${id}/cascade-preview`),
};

export const brandsApi = {
  create: (data) => api.post('/brands', data),
  getAll: (params) => api.get('/brands', { params }),
  getById: (id) => api.get(`/brands/${id}`),
  update: (id, data) => api.put(`/brands/${id}`, data),
  delete: (id) => api.delete(`/brands/${id}`, { params: { confirm: true } }),
  cascadePreview: (id) => api.get(`/brands/${id}/cascade-preview`),
  getTimezones: () => api.get('/brands/timezones'),
};

export const productsApi = {
  create: (data) => api.post('/products', data),
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

export const salesTeamApi = {
  create: (data) => api.post('/sales-team', data),
  getAll: (params) => api.get('/sales-team', { params }),
  getById: (id) => api.get(`/sales-team/${id}`),
  update: (id, data) => api.put(`/sales-team/${id}`, data),
  delete: (id) => api.delete(`/sales-team/${id}`),
  bulkDelete: () => api.delete('/sales-team/bulk-delete'),
  exportCsv: () => api.get('/sales-team/export-csv', { responseType: 'blob' }),
  parseCsv: (formData) => api.post('/sales-team/import-csv/parse', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  executeCsv: (formData) => api.post('/sales-team/import-csv/execute', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const salespersonCustomFieldsApi = {
  create: (data) => api.post('/salesperson-custom-fields', data),
  getAll: (params) => api.get('/salesperson-custom-fields', { params }),
  getById: (id) => api.get(`/salesperson-custom-fields/${id}`),
  update: (id, data) => api.put(`/salesperson-custom-fields/${id}`, data),
  delete: (id) => api.delete(`/salesperson-custom-fields/${id}`),
};

export const salespersonRoutingApi = {
  getAll: (params) => api.get('/salesperson-routing', { params }),
  create: (data) => api.post('/salesperson-routing', data),
  update: (id, data) => api.put(`/salesperson-routing/${id}`, data),
  delete: (id) => api.delete(`/salesperson-routing/${id}`),
  bulkDelete: (salesperson_id) => api.delete('/salesperson-routing/bulk-delete', { params: { salesperson_id } }),
};

export const projectTypesApi = {
  create: (data) => api.post('/project-types', data),
  getAll: (params) => api.get('/project-types', { params }),
  getById: (id) => api.get(`/project-types/${id}`),
  update: (id, data) => api.put(`/project-types/${id}`, data),
  delete: (id) => api.delete(`/project-types/${id}`),
};

export const projectStepsApi = {
  create: (data) => api.post('/project-steps', data),
  getAll: (params) => api.get('/project-steps', { params }),
  getById: (id) => api.get(`/project-steps/${id}`),
  update: (id, data) => api.put(`/project-steps/${id}`, data),
  delete: (id) => api.delete(`/project-steps/${id}`),
};

export const projectQuestionsApi = {
  create: (data) => api.post('/project-questions', data),
  getAll: (params) => api.get('/project-questions', { params }),
  getById: (id) => api.get(`/project-questions/${id}`),
  update: (id, data) => api.put(`/project-questions/${id}`, data),
  delete: (id) => api.delete(`/project-questions/${id}`),
  reorder: (data) => api.post('/project-questions/reorder', data),
};

export const assetsApi = {
  create: (data) => api.post('/assets', data),
  getAll: (params) => api.get('/assets', { params }),
  getById: (id) => api.get(`/assets/${id}`),
  update: (id, data) => api.put(`/assets/${id}`, data),
  delete: (id) => api.delete(`/assets/${id}`),
};

export const contentApi = {
  create: (data) => api.post('/content', data),
  getAll: (params) => api.get('/content', { params }),
  getById: (id) => api.get(`/content/${id}`),
  update: (id, data) => api.put(`/content/${id}`, data),
  delete: (id) => api.delete(`/content/${id}`),
  upload: (formData) => api.post('/content/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }),
  getFolders: (params) => api.get('/content/folders/list', { params }),
};

export const assetTypesApi = {
  create: (data) => api.post('/asset-types', data),
  getAll: (params) => api.get('/asset-types', { params }),
  getById: (id) => api.get(`/asset-types/${id}`),
  update: (id, data) => api.put(`/asset-types/${id}`, data),
  delete: (id) => api.delete(`/asset-types/${id}`),
};

export const assetStepsApi = {
  create: (data) => api.post('/asset-steps', data),
  getAll: (params) => api.get('/asset-steps', { params }),
  getById: (id) => api.get(`/asset-steps/${id}`),
  update: (id, data) => api.put(`/asset-steps/${id}`, data),
  delete: (id) => api.delete(`/asset-steps/${id}`),
};

export const assetQuestionsApi = {
  create: (data) => api.post('/asset-questions', data),
  getAll: (params) => api.get('/asset-questions', { params }),
  getById: (id) => api.get(`/asset-questions/${id}`),
  update: (id, data) => api.put(`/asset-questions/${id}`, data),
  delete: (id) => api.delete(`/asset-questions/${id}`),
  reorder: (data) => api.post('/asset-questions/reorder', data),
};

export const productCategoriesApi = {
  create: (data) => api.post('/product-categories', data),
  getAll: (params) => api.get('/product-categories', { params }),
  getById: (id) => api.get(`/product-categories/${id}`),
  update: (id, data) => api.put(`/product-categories/${id}`, data),
  delete: (id) => api.delete(`/product-categories/${id}`),
};

export const priceListsApi = {
  create: (data) => api.post('/price-lists', data),
  getAll: (params) => api.get('/price-lists', { params }),
  getById: (id) => api.get(`/price-lists/${id}`),
  update: (id, data) => api.put(`/price-lists/${id}`, data),
  delete: (id) => api.delete(`/price-lists/${id}`),
  addCustomers: (id, customer_ids) => api.post(`/price-lists/${id}/customers`, { customer_ids }),
  removeCustomer: (id, customer_id) => api.delete(`/price-lists/${id}/customers/${customer_id}`),
  getCustomers: (id, params) => api.get(`/price-lists/${id}/customers`, { params }),
  getByCustomer: (customer_id) => api.get(`/price-lists/by-customer/${customer_id}`),
};

export const ordersApi = {
  create: (data) => api.post('/orders', data),
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  update: (id, data) => api.put(`/orders/${id}`, data),
  delete: (id) => api.delete(`/orders/${id}`),
  deleteByIds: (ids) => api.delete('/orders/bulk/by-ids', { data: { ids } }),
  deleteAll: () => api.delete('/orders/bulk/all'),
  calendarSummary: (params) => api.get('/orders/calendar/summary', { params }),
  sourcesBreakdown: ({ days, dateFrom, dateTo } = {}) =>
    api.get('/orders/sources/breakdown', { params: { days, date_from: dateFrom, date_to: dateTo } }),
  sourcesList: () => api.get('/orders/sources/list'),
  export: (params) => api.get('/orders/export', { params, responseType: 'blob' }),
};

export const externalSyncApi = {
  pushOrders: (params = {}) => api.post('/external-sync/push-orders', null, { params }),
};

export const orderStatusesApi = {
  create: (data) => api.post('/order-statuses', data),
  getAll: (params) => api.get('/order-statuses', { params }),
  getActive: () => api.get('/order-statuses/active'),
  getById: (id) => api.get(`/order-statuses/${id}`),
  update: (id, data) => api.put(`/order-statuses/${id}`, data),
  delete: (id) => api.delete(`/order-statuses/${id}`),
};

export const projectsApi = {
  create: (data) => api.post('/projects', data),
  getAll: (params) => api.get('/projects', { params }),
  getById: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.put(`/projects/${id}`, data),
  delete: (id) => api.delete(`/projects/${id}`),
};

export const strategiesApi = {
  create: (data) => api.post('/strategies', data),
  getByProjectId: (projectId) => api.get(`/strategies/project/${projectId}`),
  getById: (id) => api.get(`/strategies/${id}`),
  update: (id, data) => api.put(`/strategies/${id}`, data),
  delete: (id) => api.delete(`/strategies/${id}`),
};

export const briefsApi = {
  create: (data) => api.post('/briefs', data),
  getByProjectId: (projectId) => api.get(`/briefs/project/${projectId}`),
  getById: (id) => api.get(`/briefs/${id}`),
  update: (id, data) => api.put(`/briefs/${id}`, data),
  delete: (id) => api.delete(`/briefs/${id}`),
};

export const briefTriggersApi = {
  create: (data) => api.post('/brief-triggers', data),
  getByProjectId: (projectId, params) => api.get(`/brief-triggers/project/${projectId}`, { params }),
  getById: (id) => api.get(`/brief-triggers/${id}`),
  update: (id, data) => api.put(`/brief-triggers/${id}`, data),
  delete: (id) => api.delete(`/brief-triggers/${id}`),
  toggle: (id) => api.post(`/brief-triggers/${id}/toggle`),
};

export const projectTriggersApi = {
  create: (data) => api.post('/project-triggers', data),
  getAll: (params) => api.get('/project-triggers', { params }),
  getByProjectId: (projectId, params) => api.get(`/project-triggers/project/${projectId}`, { params }),
  getById: (id) => api.get(`/project-triggers/${id}`),
  update: (id, data) => api.put(`/project-triggers/${id}`, data),
  delete: (id) => api.delete(`/project-triggers/${id}`),
  toggle: (id) => api.post(`/project-triggers/${id}/toggle`),
};

export const journeyTriggersApi = {
  getAll: (params) => api.get('/journey-triggers', { params }),
  getById: (id) => api.get(`/journey-triggers/${id}`),
  create: (data) => api.post('/journey-triggers', data),
  update: (id, data) => api.put(`/journey-triggers/${id}`, data),
  delete: (id) => api.delete(`/journey-triggers/${id}`),
  toggle: (id) => api.patch(`/journey-triggers/${id}/toggle`),
  validateCron: (cron_expression, timezone) => api.post('/journey-triggers/validate-cron', null, { 
    params: { cron_expression, timezone } 
  }),
  previewRuns: (id, count) => api.get(`/journey-triggers/${id}/preview-runs`, { params: { count } }),
};

export const journeysApi = {
  create: (data) => api.post('/journeys', data),
  getAll: (params) => api.get('/journeys', { params }),
  getById: (id) => api.get(`/journeys/${id}`),
  update: (id, data) => api.put(`/journeys/${id}`, data),
  delete: (id) => api.delete(`/journeys/${id}`),
};

export const journeyExecutionsApi = {
  trigger: (data) => api.post('/journey-executions/trigger', null, { params: data }),
  getAll: (params) => api.get('/journey-executions/', { params }),
  getById: (executionId) => api.get(`/journey-executions/executions/${executionId}`),
  getInstances: (executionId, params) => api.get(`/journey-executions/executions/${executionId}/instances`, { params }),
  getInstance: (instanceId) => api.get(`/journey-executions/instances/${instanceId}`),
  cancelInstance: (instanceId, reason) => api.post(`/journey-executions/instances/${instanceId}/cancel`, { reason }),
  deleteAll: () => api.delete('/journey-executions/all'),
};

export const customersApi = {
  create: (data) => api.post('/customers', data),
  getAll: (params) => api.get('/customers', { params }),
  getById: (id) => api.get(`/customers/${id}`),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  requestOTP: (data) => api.post('/customers/otp/request', data),
  verifyOTP: (data) => api.post('/customers/otp/verify', data),
  ensureMediaFolder: (customerId) => api.post(`/customer-media/ensure-folder/${customerId}`),
  importPreview: (formData) => api.post('/customers/import/preview', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  importExecute: (formData) => api.post('/customers/import/execute', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Unified behavioral timeline (events + memory points merged + sorted)
  getTimeline: (customerId, params) => api.get(`/customers/${customerId}/timeline`, { params }),
  // Admin-only storefront readiness diagnostic + 24h session_url to walk
  // the catalog as the customer (see backend /session-simulator endpoint)
  sessionSimulator: (customerId) => api.get(`/customers/${customerId}/session-simulator`),
  // Operations diagnostic: list every customer in the current brand who is
  // not covered by any active price list (mirrors the catalog visibility
  // service). Stats returns a count for the UI badge; export streams a CSV
  // suitable to hand back to the ERP team for reconciliation.
  noPriceListStats: () => api.get('/customers/exports/no-price-list/stats'),
  noPriceListExportUrl: () => '/api/customers/exports/no-price-list',
};

export const visitSchedulesApi = {
  getAll: (params) => api.get('/visit-schedules', { params }),
  create: (data) => api.post('/visit-schedules', data),
  update: (id, data) => api.put(`/visit-schedules/${id}`, data),
  delete: (id) => api.delete(`/visit-schedules/${id}`),
  getSalespersons: (params) => api.get('/visit-schedules/salespersons', { params }),
};

export const locationsApi = {
  getAll: (params) => api.get('/locations', { params }),
  getById: (id) => api.get(`/locations/${id}`),
  create: (data) => api.post('/locations', data),
  update: (id, data) => api.put(`/locations/${id}`, data),
  delete: (id) => api.delete(`/locations/${id}`),
};

export const toolsApi = {
  getAll: (params) => api.get('/tools', { params }),
  getById: (id) => api.get(`/tools/${id}`),
  create: (data) => api.post('/tools', data),
  update: (id, data) => api.put(`/tools/${id}`, data),
  delete: (id) => api.delete(`/tools/${id}`),
  test: (id, data) => api.post(`/tools/${id}/test`, data),
  syncInternal: () => api.post('/tools/sync-internal'),
  syncProviders: () => api.post('/tools/sync-providers'),
  deleteAll: () => api.delete('/tools/bulk/delete-all'),
  getCategories: (params) => api.get('/tool-categories', { params }),
  // Public-tool approval workflow
  requestPublic: (id) => api.put(`/tools/${id}`, { visibility: 'public' }),
  listPendingApproval: () => api.get('/tools/platform/pending-approval'),
  approve: (id) => api.post(`/tools/${id}/approve`),
  reject: (id, reason) => api.post(`/tools/${id}/reject`, null, { params: reason ? { reason } : {} }),
};

export const toolCategoriesApi = {
  getAll: (params) => api.get('/tool-categories', { params }),
  getById: (id) => api.get(`/tool-categories/${id}`),
  create: (data) => api.post('/tool-categories', data),
  update: (id, data) => api.put(`/tool-categories/${id}`, data),
  delete: (id) => api.delete(`/tool-categories/${id}`),
  deleteAll: (params) => api.delete('/tool-categories', { params }),
};

export const customFieldsApi = {
  create: (data) => api.post('/custom-fields', data),
  getAll: (params) => api.get('/custom-fields', { params }),
  getPublic: (brandId) => api.get(`/custom-fields/public/${brandId}`),
  getById: (id) => api.get(`/custom-fields/${id}`),
  update: (id, data) => api.put(`/custom-fields/${id}`, data),
  delete: (id) => api.delete(`/custom-fields/${id}`),
};

export const businessRulesApi = {
  create: (data) => api.post('/business-rules', data),
  getAll: (params) => api.get('/business-rules', { params }),
  getById: (id) => api.get(`/business-rules/${id}`),
  update: (id, data) => api.put(`/business-rules/${id}`, data),
  delete: (id) => api.delete(`/business-rules/${id}`),
};

export const brandBehaviorTypesApi = {
  create: (data) => api.post('/brand-behavior-types', data),
  getAll: (params) => api.get('/brand-behavior-types', { params }),
  getById: (id) => api.get(`/brand-behavior-types/${id}`),
  update: (id, data) => api.put(`/brand-behavior-types/${id}`, data),
  delete: (id) => api.delete(`/brand-behavior-types/${id}`),
  getCategories: () => api.get('/brand-behavior-types/categories/list'),
};

export const brandBehaviorSubscribersApi = {
  create: (data) => api.post('/brand-behavior-subscribers', data),
  getAll: (params) => api.get('/brand-behavior-subscribers', { params }),
  getById: (id) => api.get(`/brand-behavior-subscribers/${id}`),
  update: (id, data) => api.put(`/brand-behavior-subscribers/${id}`, data),
  delete: (id) => api.delete(`/brand-behavior-subscribers/${id}`),
};

export const brandEventsApi = {
  getAll: (params) => api.get('/brand-events', { params }),
  getById: (id) => api.get(`/brand-events/${id}`),
  create: (data) => api.post('/brand-events', data),
  update: (id, data) => api.put(`/brand-events/${id}`, data),
  delete: (id) => api.delete(`/brand-events/${id}`),
  getEventTypes: () => api.get('/brand-events/types/list'),
};

export const productCustomFieldsApi = {
  create: (data) => api.post('/product-custom-fields', data),
  getAll: (params) => api.get('/product-custom-fields', { params }),
  getPublic: (brandId) => api.get(`/product-custom-fields/public/${brandId}`),
  getFilterable: () => api.get('/product-custom-fields/filterable-fields'),
  getById: (id) => api.get(`/product-custom-fields/${id}`),
  update: (id, data) => api.put(`/product-custom-fields/${id}`, data),
  delete: (id) => api.delete(`/product-custom-fields/${id}`),
};

export const customerSegmentsApi = {
  create: (data) => api.post('/customer-segments', data),
  getAll: (params) => api.get('/customer-segments', { params }),
  getById: (id) => api.get(`/customer-segments/${id}`),
  update: (id, data) => api.put(`/customer-segments/${id}`, data),
  delete: (id) => api.delete(`/customer-segments/${id}`),
  getFields: () => api.get('/customer-segments/fields'),
  getCustomers: (id, params) => api.get(`/customer-segments/${id}/customers`, { params }),
  recalculate: (id) => api.post(`/customer-segments/${id}/recalculate`),
  importCsv: (formData) => api.post('/customer-segments/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

export const broadcastsApi = {
  list: (params) => api.get('/broadcasts', { params }),
  get: (id) => api.get(`/broadcasts/${id}`),
  metrics: (id) => api.get(`/broadcasts/${id}/metrics`),
  create: (data) => api.post('/broadcasts', data),
  cancel: (id) => api.post(`/broadcasts/${id}/cancel`),
  remove: (id) => api.delete(`/broadcasts/${id}`),
  recipients: (id, params) => api.get(`/broadcasts/${id}/recipients`, { params }),
  keyFieldSuggestion: (channelId) => api.get('/broadcasts/key-field-suggestion', { params: { channel_id: channelId } }),
  templates: (channelId) => api.get('/broadcasts/templates', { params: { channel_id: channelId } }),
  segmentsForChannel: (channelId, keyField) =>
    api.get('/broadcasts/segments-for-channel', { params: { channel_id: channelId, key_field: keyField } }),
  templateFieldOptions: () => api.get('/broadcasts/template-field-options'),
  recipientsExport: (id) => api.get(`/broadcasts/${id}/recipients/export`, { responseType: 'blob' }),
};

export const customerActivityLogsApi = {
  create: (data) => api.post('/customer-activity-logs', data),
  getByCustomer: (customerId, params) => api.get(`/customer-activity-logs/customer/${customerId}`, { params }),
  getStats: (customerId) => api.get(`/customer-activity-logs/stats/${customerId}`),
  delete: (id) => api.delete(`/customer-activity-logs/${id}`),
  createBulk: (data) => api.post('/customer-activity-logs/bulk', data),
};

export const behaviorTypesApi = {
  create: (data) => api.post('/behavior-types', data),
  getAll: (params) => api.get('/behavior-types', { params }),
  getById: (id) => api.get(`/behavior-types/${id}`),
  update: (id, data) => api.put(`/behavior-types/${id}`, data),
  delete: (id) => api.delete(`/behavior-types/${id}`),
  getCategories: () => api.get('/behavior-types/categories/list'),
};

export const behaviorSubscribersApi = {
  create: (data) => api.post('/behavior-subscribers', data),
  getAll: (params) => api.get('/behavior-subscribers', { params }),
  getById: (id) => api.get(`/behavior-subscribers/${id}`),
  update: (id, data) => api.put(`/behavior-subscribers/${id}`, data),
  delete: (id) => api.delete(`/behavior-subscribers/${id}`),
  getByBehavior: (behaviorTypeId) => api.get(`/behavior-subscribers/by-behavior/${behaviorTypeId}`),
};

export const brandPaymentMethodsApi = {
  list: () => api.get('/brand-payment-methods'),
  listActive: () => api.get('/brand-payment-methods/active'),
  create: (data) => api.post('/brand-payment-methods', data),
  update: (id, data) => api.put(`/brand-payment-methods/${id}`, data),
  delete: (id) => api.delete(`/brand-payment-methods/${id}`),
};

export const paymentGatewaysApi = {
  list: () => api.get('/payments/gateways'),
  saveCredentials: (gateway, credentials) => api.put(`/payments/gateways/${gateway}/credentials`, { credentials }),
  deleteCredentials: (gateway) => api.delete(`/payments/gateways/${gateway}/credentials`),
};

export const brandSyncApi = {
  meta: () => api.get('/brand-sync/meta'),
  connections: (b) => api.get('/brand-sync/connections', { params: { brand_id: b } }),
  createConnection: (b, data) => api.post('/brand-sync/connections', data, { params: { brand_id: b } }),
  updateConnection: (b, id, data) => api.put(`/brand-sync/connections/${id}`, data, { params: { brand_id: b } }),
  deleteConnection: (b, id) => api.delete(`/brand-sync/connections/${id}`, { params: { brand_id: b } }),
  generateKey: (b, data) => api.post('/brand-sync/connections/generate-key', data, { params: { brand_id: b } }),
  publicKey: (b, id) => api.get(`/brand-sync/connections/${id}/public-key`, { params: { brand_id: b } }),
  testConnection: (b, id, data) => api.post(`/brand-sync/connections/${id}/test`, data, { params: { brand_id: b } }),
  jobs: (b) => api.get('/brand-sync/jobs', { params: { brand_id: b } }),
  createJob: (b, data) => api.post('/brand-sync/jobs', data, { params: { brand_id: b } }),
  updateJob: (b, id, data) => api.put(`/brand-sync/jobs/${id}`, data, { params: { brand_id: b } }),
  deleteJob: (b, id) => api.delete(`/brand-sync/jobs/${id}`, { params: { brand_id: b } }),
  runJob: (b, id, data) => api.post(`/brand-sync/jobs/${id}/run`, data, { params: { brand_id: b }, timeout: 0 }),
  runs: (b, jobId) => api.get('/brand-sync/runs', { params: { brand_id: b, job_id: jobId } }),
  sample: (b, data) => api.post('/brand-sync/sample', data, { params: { brand_id: b } }),
  scriptExample: () => api.get('/brand-sync/script-example'),
  scriptVersions: (b, id) => api.get(`/brand-sync/jobs/${id}/script`, { params: { brand_id: b } }),
  saveScript: (b, id, data) => api.put(`/brand-sync/jobs/${id}/script`, data, { params: { brand_id: b } }),
  activateScript: (b, id, version) => api.post(`/brand-sync/jobs/${id}/script/activate/${version}`, null, { params: { brand_id: b } }),
  testScript: (b, id, data) => api.post(`/brand-sync/jobs/${id}/script/test`, data, { params: { brand_id: b }, timeout: 0 }),
};

export const agentsApi = {
  create: (data) => api.post('/agents', data),
  getAll: (params) => api.get('/agents', { params }),
  getById: (id) => api.get(`/agents/${id}`),
  update: (id, data) => api.put(`/agents/${id}`, data),
  delete: (id) => api.delete(`/agents/${id}`),
  resyncTools: (id) => api.post(`/agents/${id}/resync-tools`),
  syncToProvider: (id) => api.post(`/agents/${id}/sync-to-provider`),
  setDefaults: (id, flags) => api.post(`/agents/${id}/defaults`, flags),
};

export const agentToolsApi = {
  create: (data) => api.post('/agent-tools', data),
  getAll: (params) => api.get('/agent-tools', { params }),
  getById: (id) => api.get(`/agent-tools/${id}`),
  update: (id, data) => api.put(`/agent-tools/${id}`, data),
  delete: (id) => api.delete(`/agent-tools/${id}`),
  getToolsForAgent: (agentId, params) => api.get(`/agent-tools/agent/${agentId}/tools`, { params }),
  getAgentsForTool: (toolId, params) => api.get(`/agent-tools/tool/${toolId}/agents`, { params }),
};

export const brandPromptsApi = {
  list: () => api.get('/brand-prompts'),
  get: (slot) => api.get(`/brand-prompts/${slot}`),
  update: (slot, data) => api.put(`/brand-prompts/${slot}`, data),
  reset: (slot) => api.delete(`/brand-prompts/${slot}`),
  test: (slot, data) => api.post(`/brand-prompts/${slot}/test`, data || {}),
};

export const tasksApi = {
  create: (data) => api.post('/tasks', data),
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
};

export const flowsApi = {
  create: (data) => api.post('/flows', data),
  getAll: (params) => api.get('/flows', { params }),
  getById: (id) => api.get(`/flows/${id}`),
  update: (id, data) => api.put(`/flows/${id}`, data),
  delete: (id) => api.delete(`/flows/${id}`),
  addTask: (flowId, taskId) => api.post(`/flows/${flowId}/tasks/${taskId}`),
  removeTask: (flowId, taskId) => api.delete(`/flows/${flowId}/tasks/${taskId}`),
  reorderTasks: (flowId, taskIds) => api.put(`/flows/${flowId}/tasks/reorder`, { task_ids: taskIds }),
};

export const queuesApi = {
  create: (data) => api.post('/queues', data),
  getAll: (params) => api.get('/queues', { params }),
  getById: (id) => api.get(`/queues/${id}`),
  update: (id, data) => api.put(`/queues/${id}`, data),
  delete: (id) => api.delete(`/queues/${id}`),
  deleteAll: () => api.delete('/queues/all'),
  addFlow: (queueId, flowId) => api.post(`/queues/${queueId}/flows/${flowId}`),
  removeFlow: (queueId, flowId) => api.delete(`/queues/${queueId}/flows/${flowId}`),
  execute: (queueId, data) => api.post(`/queues/${queueId}/execute`, data),
  getLogs: (queueId) => api.get(`/queues/${queueId}/logs`),
  deleteLogs: (queueId) => api.delete(`/queues/${queueId}/logs`),
  emergencyStopAll: () => api.post('/queues/emergency-stop-all'),
};

export const configApi = {
  create: (data) => api.post('/config', data),
  getAll: (params) => api.get('/config', { params }),
  getCategories: () => api.get('/config/categories'),
  getById: (id) => api.get(`/config/${id}`),
  update: (id, data) => api.put(`/config/${id}`, data),
  delete: (id) => api.delete(`/config/${id}`),
};

export const logsApi = {
  getAll: (params) => api.get('/logs', { params }),
  getById: (id) => api.get(`/logs/${id}`),
  deleteAll: () => api.delete('/logs/all'),
};

export const webhooksApi = {
  verifyWhatsApp: (params) => api.get('/webhooks/whatsapp/verify', { params }),
  receiveWhatsApp: (data) => api.post('/webhooks/whatsapp/receive', data),
};

export const providersApi = {
  create: (data) => api.post('/providers', data),
  getAll: (params) => api.get('/providers', { params }),
  getById: (id) => api.get(`/providers/${id}`),
  update: (id, data) => api.put(`/providers/${id}`, data),
  delete: (id) => api.delete(`/providers/${id}`),
  cleanupControllers: () => api.post('/providers/cleanup-controllers'),
  getLogs: (id, params) => api.get(`/providers/${id}/logs`, { params }),
  deleteAllLogs: () => api.delete('/providers/logs/all'),
};

export const channelsApi = {
  create: (data) => api.post('/channels', data),
  getAll: (params) => api.get('/channels', { params }),
  getById: (id) => api.get(`/channels/${id}`),
  update: (id, data) => api.put(`/channels/${id}`, data),
  delete: (id) => api.delete(`/channels/${id}`),
  getProviders: (id) => api.get(`/channels/${id}/providers`),
  getAgents: (id) => api.get(`/channels/${id}/agents`),
  getFlows: (id) => api.get(`/channels/${id}/flows`),
  getJobCategories: (id) => api.get(`/channels/${id}/job-categories`),
  updateConfiguration: (id, data) => api.put(`/channels/${id}/configuration`, data),
};

export const jobCategoriesApi = {
  create: (data) => api.post('/job-categories', data),
  getAll: (params) => api.get('/job-categories', { params }),
  getById: (id) => api.get(`/job-categories/${id}`),
  update: (id, data) => api.put(`/job-categories/${id}`, data),
  delete: (id) => api.delete(`/job-categories/${id}`),
};

export const jobsApi = {
  create: (data) => api.post('/jobs', data),
  getAll: (params) => api.get('/jobs', { params }),
  getById: (id) => api.get(`/jobs/${id}`),
  update: (id, data) => api.put(`/jobs/${id}`, data),
  delete: (id) => api.delete(`/jobs/${id}`),
  deleteAll: () => api.delete('/jobs/bulk/delete-all'),
};

export const triggersApi = {
  create: (data) => api.post('/triggers', data),
  getAll: (params) => api.get('/triggers', { params }),
  getById: (id) => api.get(`/triggers/${id}`),
  update: (id, data) => api.put(`/triggers/${id}`, data),
  delete: (id) => api.delete(`/triggers/${id}`),
};

export const customerSuppressionApi = {
  list: (params) => api.get('/customer-suppression', { params }),
  create: (data) => api.post('/customer-suppression', data),
  remove: (customerId) => api.delete(`/customer-suppression/${customerId}`),
  export: (params) => api.get('/customer-suppression/export', { params, responseType: 'blob' }),
};

export const channelTypesApi = {
  create: (data) => api.post('/channel-types', data),
  getAll: (params) => api.get('/channel-types', { params }),
  getById: (id) => api.get(`/channel-types/${id}`),
  update: (id, data) => api.put(`/channel-types/${id}`, data),
  delete: (id) => api.delete(`/channel-types/${id}`),
};

export const cronJobsApi = {
  create: (data) => api.post('/cron-jobs', data),
  getAll: (params) => api.get('/cron-jobs', { params }),
  getById: (id) => api.get(`/cron-jobs/${id}`),
  update: (id, data) => api.put(`/cron-jobs/${id}`, data),
  delete: (id) => api.delete(`/cron-jobs/${id}`),
  run: (id) => api.post(`/cron-jobs/${id}/run`),
};

export const mediaActionsApi = {
  getAll: (params) => api.get('/media-actions', { params }),
  getById: (id) => api.get(`/media-actions/${id}`),
  create: (data) => api.post('/media-actions', data),
  update: (id, data) => api.put(`/media-actions/${id}`, data),
  delete: (id) => api.delete(`/media-actions/${id}`),
  execute: (id) => api.post(`/media-actions/${id}/execute`),
  getLogs: (id, params) => api.get(`/media-actions/${id}/logs`, { params }),
};

export const customerEventsApi = {
  getAll: (params) => api.get('/customer-events', { params }),
  getById: (id) => api.get(`/customer-events/${id}`),
  create: (data) => api.post('/customer-events', data),
  update: (id, data) => api.put(`/customer-events/${id}`, data),
  delete: (id) => api.delete(`/customer-events/${id}`),
  getEventTypes: () => api.get('/customer-events/types/list'),
};

export const orderTriggersApi = {
  getAll: (params) => api.get('/order-triggers', { params }),
  getById: (id) => api.get(`/order-triggers/${id}`),
  create: (data) => api.post('/order-triggers', data),
  update: (id, data) => api.put(`/order-triggers/${id}`, data),
  delete: (id) => api.delete(`/order-triggers/${id}`),
  toggle: (id) => api.patch(`/order-triggers/${id}/toggle`),
};

// Conversation Queues API
export const conversationQueuesApi = {
  getAll: (params) => api.get('/conversation-queues', { params }),
  getById: (id) => api.get(`/conversation-queues/${id}`),
  create: (data) => api.post('/conversation-queues', data),
  update: (id, data) => api.put(`/conversation-queues/${id}`, data),
  delete: (id) => api.delete(`/conversation-queues/${id}`),
  deleteAll: (params) => api.delete('/conversation-queues', { params }),
  assignAgent: (id, agent_id) => api.post(`/conversation-queues/${id}/assign`, { agent_id }),
  getStats: (params) => api.get('/conversation-queues/stats/summary', { params }),
  getHistory: (id, params) => api.get(`/conversation-queues/${id}/history`, { params }),
  sendMessage: (id, data) => api.post(`/conversation-queues/${id}/send-message`, data),
};

// Public API instance without authentication
export const publicApi = axios.create({
  baseURL: `${baseURL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Public API methods for project workflow (no authentication required)
export const publicProjectTypesApi = {
  getAll: (params) => publicApi.get('/public/project-types', { params }),
};

export const publicProjectStepsApi = {
  getAll: (params) => publicApi.get('/public/project-steps', { params }),
};

export const publicProjectQuestionsApi = {
  getAll: (params) => publicApi.get('/public/project-questions', { params }),
};

export const publicWorkflowApi = {
  submitWorkflow: (data) => publicApi.post('/public/workflow-submit', data),
};

export const publicBrandsApi = {
  getBrand: (brandId) => publicApi.get(`/public/brands/${brandId}`),
};


// Shopping Carts API
export const shoppingCartsApi = {
  getCustomerCarts: (customerId, params) => api.get(`/customers/${customerId}/shopping-carts`, { params }),
  getCart: (cartId) => api.get(`/shopping-carts/${cartId}`),
  createCart: (data) => api.post('/shopping-carts', data),
  updateCart: (cartId, data) => api.put(`/shopping-carts/${cartId}`, data),
  deleteCart: (cartId) => api.delete(`/shopping-carts/${cartId}`),
  closeCart: (cartId, orderId) => api.post(`/shopping-carts/${cartId}/close`, { order_id: orderId }),
};

// Messages & Conversations API
export const messagesApi = {
  getAll: (params) => api.get('/messages', { params }),
  getById: (id) => api.get(`/messages/${id}`),
  getByConversation: (conversationId, params) => api.get(`/messages/conversation/${conversationId}`, { params }),
  send: (data) => api.post('/messages/send', data),
  uploadMedia: (file, conversationQueueId) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversation_queue_id', conversationQueueId);
    return api.post('/messages/upload-media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  retry: (id) => api.post(`/messages/${id}/retry`),
  getDeadLetterQueue: (params) => api.get('/messages/dead-letter-queue', { params }),
};

export const conversationsApi = {
  getAll: (params) => api.get('/conversation-queues', { params }),
  getById: (id) => api.get(`/conversation-queues/${id}`),
  getHistory: (id) => api.get(`/conversation-queues/${id}/history`),
  sendMessage: (id, data) => api.post(`/conversation-queues/${id}/send-message`, data),
  assign: (id, data) => api.post(`/conversation-queues/${id}/assign`, data),
  switchChannel: (id, data) => api.post(`/conversation-queues/${id}/switch-channel`, data),
  getAvailableChannels: (id) => api.get(`/conversation-queues/${id}/available-channels`),
  getStats: () => api.get('/conversation-queues/stats/summary'),
};

export const platformSettingsApi = {
  get: () => api.get('/platform-settings'),
  update: (data) => api.put('/platform-settings', data),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/platform-settings/upload-logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadFavicon: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/platform-settings/upload-favicon', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadLoginBackground: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/platform-settings/upload-login-background', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
};


// Warehouses API
export const warehousesApi = {
  getAll: (params) => api.get('/warehouses', { params }),
  getById: (id) => api.get(`/warehouses/${id}`),
  create: (data) => api.post('/warehouses', data),
  update: (id, data) => api.put(`/warehouses/${id}`, data),
  delete: (id) => api.delete(`/warehouses/${id}`),
};

// Inventory API
export const inventoryApi = {
  getAll: (params) => api.get('/inventory', { params }),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  delete: (id) => api.delete(`/inventory/${id}`),
  getMovements: (params) => api.get('/inventory/movements/list', { params }),
  createMovement: (data) => api.post('/inventory/movements', data),
  getAlerts: () => api.get('/inventory/alerts/list'),
  getDashboard: () => api.get('/inventory/dashboard/summary'),
};

export const geoLocationsApi = {
  getCountries: (params) => api.get('/geo-locations/countries', { params }),
  getCountry: (id) => api.get(`/geo-locations/countries/${id}`),
  createCountry: (data) => api.post('/geo-locations/countries', data),
  updateCountry: (id, data) => api.put(`/geo-locations/countries/${id}`, data),
  deleteCountry: (id) => api.delete(`/geo-locations/countries/${id}`),
  setCountryLanguages: (id, languageIds) => api.put(`/geo-locations/countries/${id}/languages`, languageIds),
  getRegions: () => api.get('/geo-locations/regions'),
  getCities: (params) => api.get('/geo-locations/cities', { params }),
  createCity: (data) => api.post('/geo-locations/cities', data),
  updateCity: (id, data) => api.put(`/geo-locations/cities/${id}`, data),
  deleteCity: (id) => api.delete(`/geo-locations/cities/${id}`),
  getLanguages: (params) => api.get('/geo-locations/languages', { params }),
  getLanguage: (id) => api.get(`/geo-locations/languages/${id}`),
  createLanguage: (data) => api.post('/geo-locations/languages', data),
  updateLanguage: (id, data) => api.put(`/geo-locations/languages/${id}`, data),
  deleteLanguage: (id) => api.delete(`/geo-locations/languages/${id}`),
  seed: () => api.post('/geo-locations/seed'),
};

export default api;

export const gameBoardsApi = {
  getAll: (params) => api.get('/game-boards', { params }),
  getById: (id) => api.get(`/game-boards/${id}`),
  create: (data) => api.post('/game-boards', data),
  update: (id, data) => api.put(`/game-boards/${id}`, data),
  delete: (id) => api.delete(`/game-boards/${id}`),
  pause: (id) => api.post(`/game-boards/${id}/pause`),
  resume: (id) => api.post(`/game-boards/${id}/resume`),
  generatePlan: (id, data) => api.post(`/game-boards/${id}/generate-plan`, data || {}),
  bulkGeneratePlans: (data) => api.post('/game-boards/bulk/generate-plans', data || {}),
  bulkClearPendingActions: (data) => api.post('/game-boards/bulk/clear-pending-actions', data || {}),
  dashboardStats: () => api.get('/game-boards/dashboard/stats'),
  plansStats: (params) => api.get('/game-boards/plans/stats', { params }),
  previewAction: (boardId, actionIndex) =>
    api.get(`/game-boards/${boardId}/actions/${actionIndex}/preview`),
  getExecutorSettings: () => api.get('/game-boards/settings/executor'),
  setExecutorSettings: (executorEnabled) => api.put('/game-boards/settings/executor', { executor_enabled: executorEnabled }),
  previewForCustomer: (customerId) => api.post(`/game-boards/customer/${customerId}/preview-plan`),
};

export const channelRulesApi = {
  getAll: (params) => api.get('/channel-rules', { params }),
  getById: (id) => api.get(`/channel-rules/${id}`),
  create: (data) => api.post('/channel-rules', data),
  update: (id, data) => api.put(`/channel-rules/${id}`, data),
  delete: (id) => api.delete(`/channel-rules/${id}`),
  toggle: (id) => api.put(`/channel-rules/${id}/toggle`),
  reorder: (data) => api.put('/channel-rules/reorder/bulk', data),
  getOptions: () => api.get('/channel-rules/options'),
};
